import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PartnerProductsService } from '../../services/partner-products.service';
import { PartnerProduct, ProductStatisticResponse } from '../../interfaces/partner-product.interface';
import { environment } from '../../../environments/environment';
import { of, switchMap } from 'rxjs';
import moment from 'moment';


// ── Inline dialog ─────────────────────────────────────────────────────────────
@Component({
  selector: 'app-partner-product-dialog',
  standalone: false,
  template: `
    <h2 >{{ data.item ? 'Edytuj produkt' : 'Nowy produkt' }}</h2>
    
    @if (stats?.order_count) {
      <div style="margin: 16px;">
        <h3>Statystyki</h3>
        @if (statsLoading) {
          <div style="margin: 4px 0"><mat-spinner diameter="20"></mat-spinner></div>
        }
        @if (!statsLoading && stats) {
          <div style="display:flex; gap:12px; flex-wrap:wrap; margin-bottom:12px;">
            <div class="stat-chip"><span class="stat-chip__label">Zamówienia</span><span class="stat-chip__value">{{ stats.order_count }}</span></div>
            <div class="stat-chip"><span class="stat-chip__label">Osoby</span><span class="stat-chip__value">{{ stats.unique_persons }}</span></div>
            <div class="stat-chip"><span class="stat-chip__label">Punkty</span><span class="stat-chip__value">{{ stats.total_score }}</span></div>
          </div>
        }
      </div>
    }
    
    <mat-dialog-content>
      <form [formGroup]="form" class="product-form">
    
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Nazwa</mat-label>
          <input matInput formControlName="name" />
          @if (form.get('name')?.invalid) {
            <mat-error>Nazwa jest wymagana</mat-error>
          }
        </mat-form-field>
    
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Opis</mat-label>
          <textarea matInput formControlName="description" rows="3"></textarea>
        </mat-form-field>
    
        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Punkty (score)</mat-label>
            <input matInput type="number" formControlName="score" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Ilość produktów</mat-label>
            <input matInput type="number" formControlName="amount" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Kolejność (sort)</mat-label>
            <input matInput type="number" formControlName="sort" />
          </mat-form-field>
        </div>
    
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Ważny do</mat-label>
          <input matInput [matDatepicker]="validPicker" formControlName="valid" />
          <mat-datepicker-toggle matSuffix [for]="validPicker"></mat-datepicker-toggle>
          <mat-datepicker #validPicker></mat-datepicker>
        </mat-form-field>
    
        <mat-slide-toggle formControlName="is_active" class="active-toggle">Aktywny</mat-slide-toggle>
    
        @if (data.item?.file) {
          <div class="file-section">
            <img [src]="imagesUrl + '/partner_products/thumb/' + data.item!.file" class="preview-image" alt="preview" />
          </div>
        }
    
        <div class="file-input">
          <button type="button" mat-stroked-button (click)="fileInput.click()">
            <mat-icon>upload</mat-icon>
            {{ selectedFile ? selectedFile.name : 'Wybierz zdjęcie' }}
          </button>
          <input #fileInput type="file" accept="image/*" hidden (change)="onFileSelected($event)" />
        </div>
    
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Anuluj</button>
      <button type="submit" mat-raised-button class="custom-button"  (click)="save()"  [disabled]="form.invalid || saving || (!form.dirty && !selectedFile)">>
        @if (saving) {
          <mat-spinner diameter="18"></mat-spinner>
        }
        {{ saving ? '' : 'Zapisz' }}
      </button>
    </mat-dialog-actions>
    `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    .product-form { display: flex; flex-direction: column; gap: 8px; min-width: 320px; padding-top: 8px; }
    .full-width { width: 100%; }
    .dialog__surface {background: #fff !important; border-radius: 8px;}
    .mat-mdc-dialog-surface { background: #fff !important; border-radius: 8px; }
    .form-row { display: flex; gap: 12px; flex-wrap: wrap; }
    .form-row mat-form-field { flex: 1; min-width: 100px; }
    .active-toggle { margin: 4px 0 8px; }
    .preview-image { max-height: 100px; border-radius: 4px; margin-bottom: 8px; display: block; }
    .file-input { margin: 4px 0; }
    .stat-chip { display:flex; flex-direction:column; align-items:center; background:#f5f5f5; border-radius:8px; padding:8px 16px; min-width:80px; }
    .stat-chip__label { font-size:11px; color:#777; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:2px; }
    .stat-chip__value { font-size:20px; font-weight:600; color:#333; }
  `]
})
export class PartnerProductDialogComponent implements OnInit {
  form: FormGroup;
  saving = false;
  selectedFile?: File;
  imagesUrl = environment.ImagesUrl;
  stats: ProductStatisticResponse | null = null;
  statsLoading = false;

  constructor(
    private fb: FormBuilder,
    private service: PartnerProductsService,
    private dialogRef: MatDialogRef<PartnerProductDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { item?: PartnerProduct; partnerId?: number },
    private snackBar: MatSnackBar
  ) {
    const i = data.item;
    this.form = this.fb.group({
      name: [i?.name ?? '', Validators.required],
      description: [i?.description ?? ''],
      score: [i?.score ?? 0],
      amount: [i?.amount ?? 0],
      sort: [i?.sort ?? 0],
      valid: [i?.valid ? new Date(i.valid.substring(0, 10) + 'T12:00:00') : null],
      is_active: [i?.is_active === 1],
    });
  }

  ngOnInit(): void {
    if (this.data.item?.id) {
      this.statsLoading = true;
      this.service.getProductStatistics(this.data.item.id).subscribe({
        next: (res) => { this.stats = res; this.statsLoading = false; },
        error: () => { this.statsLoading = false; }
      });
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0];
    this.form.markAsDirty();
  }

  save(): void {
    if (this.form.invalid) return;
    this.saving = true;

    const validVal = this.form.value.valid;
    const basePayload: Partial<PartnerProduct> = {
      ...this.form.value,
      is_active: this.form.value.is_active ? 1 : 0,
      partner_id: this.data.item?.partner_id ?? this.data.partnerId,
      valid: validVal ? moment(validVal).format('YYYY-MM-DD') : null,
    };

    // 1. Upload file first (if selected) to get the hashed filename,
    //    then include it in the create/update payload.
    const upload$ = this.selectedFile
      ? this.service.uploadFile(this.selectedFile)
      : of(null);

    upload$.pipe(
      switchMap((uploadRes: any) => {
        const payload: Partial<PartnerProduct> = uploadRes?.file_name
          ? { ...basePayload, file: uploadRes.file_name }
          : basePayload;
        return this.data.item
          ? this.service.update(this.data.item.id, payload)
          : this.service.create(payload);
      })
    ).subscribe({
      next: () => this.dialogRef.close(true),
      error: () => {
        this.saving = false;
        this.snackBar.open('Błąd podczas zapisywania', 'Zamknij', { duration: 3000, panelClass: ['error-snackbar'] });
      },
    });
  }
}




// ── List component ────────────────────────────────────────────────────────────
@Component({
  selector: 'app-partner-products',
  standalone: false,
  templateUrl: './partner-products.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./partner-products.component.scss']
})
export class PartnerProductsComponent implements OnInit {
  items: PartnerProduct[] = [];
  loading = false;
  total = 0;
  pageSize = 20;
  pageIndex = 0;
  imagesUrl = environment.ImagesUrl;
  displayedColumns = ['id', 'file', 'name', 'score', 'amount', 'sort', 'is_active', 'valid', 'actions'];

  constructor(
    private service: PartnerProductsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.loadList();
  }

  loadList(): void {
    this.loading = true;
    this.service.getCurrentList({
      order_by: 'sort',
      order: 'desc',
      skip: this.pageIndex * this.pageSize,
      limit: this.pageSize,
    }).subscribe({
      next: (res) => {
        this.items = res.items || [];
        this.total = res.total ?? this.items.length;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Błąd ładowania listy', 'Zamknij', { duration: 3000, panelClass: ['error-snackbar'] });
      },
    });
  }

  openDialog(item?: PartnerProduct): void {
    const ref = this.dialog.open(PartnerProductDialogComponent, {
      width: '90vw',
      maxWidth: '700px',
      maxHeight: '80vh',
      data: { item },
    });
    ref.afterClosed().subscribe(reload => { if (reload) this.loadList(); });
  }

  delete(item: PartnerProduct): void {
    if (!confirm(`Czy na pewno chcesz usunąć „${item.name}"?`)) return;
    this.service.delete(item.id).subscribe({
      next: () => {
        this.snackBar.open('Usunięto produkt', 'Zamknij', { duration: 2500 });
        this.loadList();
      },
      error: () => this.snackBar.open('Błąd podczas usuwania', 'Zamknij', { duration: 3000, panelClass: ['error-snackbar'] }),
    });
  }

  changeSort(item: PartnerProduct, direction: 'UP' | 'DOWN'): void {
    this.service.changeSort(item.id, direction).subscribe(() => this.loadList());
  }

  onPageChange(event: any): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadList();
  }
}
